/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable max-lines */
import { HIRING_RELIABILITY_CONFIG } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import {
  BOOKING_REORDERING_COHORT,
  type PlacementCandidate,
  PlacementCandidateScreeningStatus,
} from "@src/appV2/redesign/PlacementCandidate/types";
import { useCreateShoppableQualification } from "@src/appV2/redesign/Roles/api/useCreateShoppableQualification";
import { INTERVIEW_TYPE } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useMaxReferralBonusAmount } from "@src/appV2/Referral/Bonus/flags";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { type QueryObserverResult } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useCallback, useRef } from "react";

import { useCreateChildcareSupportChat } from "../../../api/useCreateChildcareSupportChat";
import { PLACEMENT_TYPE } from "../../../api/useCreatePlacementApplication";
import {
  type GetAvailabilityResponse,
  type InterviewAvailability,
} from "../../../api/useGetInterviewAvailability";
import { type PlacementDetail } from "../../../api/useGetPlacement";
import { useBookingOrderingCohort } from "../../../hooks/useBookingOrderingCohort";
import { useJobSearchAgreement } from "../../../hooks/useJobSearchAgreement";
import {
  PlacementApplicationStatus,
  type PlacementResponse,
} from "../../../types/fetchPlacements.schema";
import { hasBookedInterview, shouldShowRequirement } from "../../../utils";
import { getWorkplaceStartHour } from "../../../utils/reliabilityRule";
import { shouldShowAgreementBeforeBooking } from "../../../utils/shouldShowAgreementBeforeBooking";
import { getVerifiedType, PLACEMENT_VERIFIED_TYPES } from "../../../utils/verified-placement";
import { type AlternativeAddressData } from "../../PlacementDetailsPage/hooks/useAlternativeAddress";
import { PLACEMENT_ACTION_TYPE, type PlacementActionType } from "../utils/getPrimaryAction";
import { useApplicationOperations } from "./operations/useApplicationOperations";
import { useInterviewOperations } from "./operations/useInterviewOperations";
import { useNavigationOperations } from "./operations/useNavigationOperations";
import { usePlacementPrimaryAction } from "./usePlacementPrimaryAction";
import { usePlacementState } from "./usePlacementState";

interface UsePlacementActionsParams {
  placementDetails: PlacementDetail;
  placementCandidate: PlacementCandidate;
  interviewAvailability: InterviewAvailability;
  isLoadingInterviewAvailability: boolean;
  alternativeAddressData?: AlternativeAddressData;
  showAlternativeAddressInformation: boolean;
  isEasySchedulingPlacement?: boolean;
  refetchPlacement: () => Promise<QueryObserverResult<PlacementResponse>>;
  refetchInterviewAvailability: () => Promise<QueryObserverResult<GetAvailabilityResponse>>;
  refetchPlacementCandidate: () => Promise<QueryObserverResult<PlacementCandidate>>;
}

/**
 * Calculate workplace start hour dynamically based on selected interview time.
 * This is needed when user selects a new time that hasn't been persisted yet.
 */
function calculateWorkplaceStartHour(params: {
  interview?: { attributes: { start: string } };
  selectedInterviewTime?: { start: Date };
  workerTimezone: string;
  workplaceTimezone: string;
}): Date | undefined {
  const { interview, selectedInterviewTime, workerTimezone, workplaceTimezone } = params;

  if (!isDefined(interview) && !isDefined(selectedInterviewTime)) {
    return undefined;
  }

  return getWorkplaceStartHour({
    interviewStart:
      selectedInterviewTime?.start ??
      (interview?.attributes.start ? parseISO(interview.attributes.start) : undefined),
    workerTimezone,
    workplaceTimezone,
    startHour: HIRING_RELIABILITY_CONFIG.WORKPLACE_START_HOUR,
  });
}

/**
 * Composes all placement action hooks and provides unified interface.
 * Component only needs to call this one hook.
 *
 * @see usePlacementState - State management
 * @see usePlacementPrimaryAction - Action selection
 * @see useApplicationOperations - Application logic
 * @see useInterviewOperations - Interview logic
 * @see useNavigationOperations - Navigation logic
 */
export function usePlacementActions(params: UsePlacementActionsParams) {
  const {
    placementDetails,
    placementCandidate,
    interviewAvailability,
    isLoadingInterviewAvailability,
    alternativeAddressData,
    showAlternativeAddressInformation,
    isEasySchedulingPlacement = false,
    refetchPlacement,
    refetchInterviewAvailability,
    refetchPlacementCandidate,
  } = params;

  const state = usePlacementState();
  const worker = useDefinedWorker();

  // Job Search Agreement check
  const { isJobSearchAgreementRequired, refetchJobSearchAgreements } = useJobSearchAgreement({
    worker,
  });

  // Get maxReferralBonusAmount early for primary action calculation
  const maxReferralBonusAmount = useMaxReferralBonusAmount("postOnboardingBonusInDollars");
  const { mutateAsync: createShoppableQualification, isLoading: isCreatingQualification } =
    useCreateShoppableQualification();
  const { mutateAsync: createChildcareSupportChat, isLoading: isCreatingChildcareSupportChat } =
    useCreateChildcareSupportChat();
  const { showSuccessToast, showErrorToast } = useToast();
  const bookingReorderingCohort = useBookingOrderingCohort();

  const primaryActionData = usePlacementPrimaryAction({
    placementDetails,
    placementCandidate,
    interviewAvailability,
    isLoadingInterviewAvailability,
    maxReferralBonusAmount,
    isEasySchedulingPlacement,
  });

  const { primaryAction, derivedData } = primaryActionData;

  const applicationOps = useApplicationOperations({
    placementDetails,
    placementCandidate,
    modals: state.modals,
    applicationState: state.applicationState,
    derivedData,
    refetchPlacement,
  });

  const interviewOps = useInterviewOperations({
    placementDetails,
    placementCandidate,
    modals: state.modals,
    interviewState: state.interviewState,
    locationState: state.locationState,
    derivedData,
    applicationOperations: applicationOps,
    refetchPlacement,
    refetchInterviewAvailability,
    refetchPlacementCandidate,
  });

  const navigationOps = useNavigationOperations({
    placementDetails,
    placementCandidate,
    primaryAction,
    derivedData,
  });

  // Pass legacyDocumentRequirementId to shouldShowRequirement to exclude the screening document
  // from the missing documents check. This allows the submitInterviewDocumentsLater modal
  // to correctly identify when only non-screening documents are missing.
  const screeningDocumentId = derivedData.legacyDocumentRequirementId;

  const hasMissingWorkerRequirements =
    placementDetails.workerRequirements?.some((requirement) =>
      shouldShowRequirement(requirement, screeningDocumentId)
    ) ?? false;
  const hasMissingCredentialRequirements =
    placementDetails.credentialRequirements?.some((requirement) =>
      shouldShowRequirement(requirement, screeningDocumentId)
    ) ?? false;
  const hasMissingOrPendingDocuments =
    hasMissingWorkerRequirements || hasMissingCredentialRequirements;

  // Ref to store the pending action callback after agreement is signed
  const pendingAgreementActionRef = useRef<(() => Promise<void> | void) | undefined>(undefined);

  /**
   * Handles agreement completion - closes agreement modal and opens confirmation.
   * Should be passed as `onComplete` to JobSearchAgreementModal.
   */
  const handleAgreementComplete = useCallback(async () => {
    state.modals.jobSearchAgreement.closeModal();
    state.modals.jobSearchAgreementConfirmation.openModal();
    // Refetch to update agreement status across the app
    await refetchJobSearchAgreements();
  }, [
    state.modals.jobSearchAgreement,
    state.modals.jobSearchAgreementConfirmation,
    refetchJobSearchAgreements,
  ]);

  /**
   * Handles confirmation CTA click - continues to booking and then closes confirmation.
   * Should be passed as `onContinueBooking` to JobSearchAgreementConfirmationModal.
   *
   * The async flow ensures the next modal (e.g., location permission or book interview)
   * opens before the confirmation modal closes, preventing the user from seeing the
   * placement detail page briefly and having to click the booking button again.
   */
  const handleAgreementContinueBooking = useCallback(async () => {
    const pendingAction = pendingAgreementActionRef.current;
    pendingAgreementActionRef.current = undefined;

    // Execute the pending action first, which will open the next modal
    // (e.g., location permission modal or book interview modal)
    await pendingAction?.();

    // Close the confirmation modal after the next modal has opened
    state.modals.jobSearchAgreementConfirmation.closeModal();
  }, [state.modals.jobSearchAgreementConfirmation]);

  // These flow methods represent complete user journeys.
  // Each flow is self-contained and easy to understand.
  //
  // Some flows have "continuation" variants that resume from a specific point
  // (e.g., after a modal closes). These are used by modal callbacks.

  const flows = {
    easySchedulingInterviewRequest: async () => {
      if (derivedData.isChildcareSupportChatEnabled && hasMissingOrPendingDocuments) {
        try {
          await createChildcareSupportChat({
            workerId: derivedData.workerId,
            placementId: placementDetails.id,
          });

          state.childcareChatState.setChildcareChatContinuationAction("easyScheduling");
          state.modals.childcareSupportChat.openModal();
          return;
        } catch {
          showErrorToast("Unable to start support chat. Please try again or contact support.");
          return;
        }
      }

      state.modals.easySchedulingInterviewRequest.openModal();

      await applicationOps.handleApply({
        placementType: PLACEMENT_TYPE.verified,
        skipSuccessModal: true,
        // Don't send workerInterviewRequest at all - it's optional
        // This is a "bare bones" application where the workplace reaches out to schedule
      });

      await Promise.all([refetchPlacement(), refetchPlacementCandidate()]);
    },

    applyToJob: async () => {
      if (derivedData.isChildcareSupportChatEnabled && hasMissingOrPendingDocuments) {
        try {
          await createChildcareSupportChat({
            workerId: derivedData.workerId,
            placementId: placementDetails.id,
          });

          state.childcareChatState.setChildcareChatContinuationAction("apply");
          state.modals.childcareSupportChat.openModal();
          return;
        } catch {
          showErrorToast("Unable to start support chat. Please try again or contact support.");
          return;
        }
      }

      await applicationOps.handleFetchApplicationPreferences(async (preferences) => {
        if (derivedData.isJobRequestEnabled) {
          state.modals.createApplication.openModal();
        } else {
          await applicationOps.handleApply({
            placementType: PLACEMENT_TYPE.verified,
            preferences,
          });
        }
      });
    },

    bookInterview: async () => {
      const verifiedType = getVerifiedType(placementDetails);
      const isInPersonInterview =
        placementDetails.interviewSettings?.attributes.selfServeInterviewBookingType ===
        INTERVIEW_TYPE.IN_PERSON;

      // If submit documents later flag is enabled, user has missing documents,
      // and this is an in-person interview, show the confirmation bottom sheet
      // before proceeding to interview booking.
      // Skip for screen_last cohort - they can bypass documents entirely for the "book first, screen later" flow
      const isChildCareCenter = placementDetails.workplace.type === "Child Care Center";

      if (
        derivedData.isSubmitHiringDocumentsLaterEnabled &&
        isChildCareCenter &&
        hasMissingOrPendingDocuments &&
        isInPersonInterview &&
        !derivedData.canBypassDocumentsForScreenLast
      ) {
        state.modals.submitInterviewDocumentsLater.openModal();
        return;
      }

      // For book_first cohort, skip all confirmation dialogs and go directly to interview booking
      if (
        derivedData.isEligibleForBookFirstConfirmation &&
        verifiedType !== PLACEMENT_VERIFIED_TYPES.APPLY_ON_WEB
      ) {
        await interviewOps.openBookInterviewModal(
          PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW,
          interviewAvailability
        );
        return;
      }

      const isProspectWorkplace = Boolean(placementDetails.workplace.placementSettings?.isProspect);
      const shouldShowAlternativeAddress =
        showAlternativeAddressInformation && isDefined(alternativeAddressData);

      if (isProspectWorkplace) {
        state.modals.walkInNote.openModal();
        // Note: Modal's onContinue callback will call the actual booking flow
        // by calling interviewOps.openBookInterviewModal directly
        return;
      }

      if (shouldShowAlternativeAddress) {
        state.modals.showAlternativeAddress.openModal();
        // Note: Modal's onContinue callback will call the actual booking flow
        // by calling interviewOps.openBookInterviewModal directly
        return;
      }

      await applicationOps.handleFetchApplicationPreferences(async (preferences) => {
        // Store preferences for the interview booking
        applicationOps.setApplicationPreferences(preferences);
        await interviewOps.openBookInterviewModal(
          PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW,
          interviewAvailability
        );
      });
    },

    rescheduleInterview: async () => {
      // Check if rescheduling today after workplace start hour
      if (derivedData.isLateCancellation && derivedData.isSchedulingTodayAfterWorkplaceStartHour) {
        state.modals.lateRescheduleToday.openModal();
        return;
      }

      // The modal handles all remaining steps via its callbacks
      await interviewOps.openBookInterviewModal(
        PLACEMENT_ACTION_TYPE.RESCHEDULE_INTERVIEW,
        interviewAvailability
      );
    },

    cancelPastInterview: () => {
      state.modals.cancelPastInterview.openModal();
    },

    completeProfile: () => {
      navigationOps.handleCompleteProfileAction();
    },

    uploadLicense: () => {
      navigationOps.handleUploadLicenseAction();
    },

    uploadDocuments: () => {
      navigationOps.handleUploadDocumentAction();
    },

    uploadCredential: () => {
      navigationOps.handleUploadCredentialAction();
    },

    addQualification: async () => {
      if (!isDefined(derivedData.missingAutoApprovedQualificationId)) {
        showErrorToast("Unable to add role. Please try again.");
        return;
      }

      try {
        await createShoppableQualification({
          workerId: derivedData.workerId,
          qualificationId: derivedData.missingAutoApprovedQualificationId,
        });

        showSuccessToast("Role added successfully!");

        // Refetch to update the UI with new data
        await Promise.all([refetchPlacement(), refetchPlacementCandidate()]);
      } catch {
        showErrorToast("Unable to add role. Please try again or contact support.");
      }
    },

    applyViaExternalLink: async () => {
      await applicationOps.openExternalApplication();
    },

    talkToRecruiter: async () => {
      await navigationOps.handleTalkToRecruiterAction();
    },

    talkToRecruiterNextSteps: () => {
      navigationOps.handleTalkToRecruiterNextStepsAction();
    },

    verifyInterviewAttendance: () => {
      state.modals.verifyInterviewAttendance.openModal();
    },

    updateInterviewEta: () => {
      state.modals.updateInterviewEta.openModal();
    },

    // These continuation flows resume a user journey from a specific point,
    // typically called from modal callbacks.

    continueBookInterview: async () => {
      const isBookFirstCohort = bookingReorderingCohort === BOOKING_REORDERING_COHORT.BOOK_FIRST;

      // For book_first cohort, skip preferences dialog and go directly to interview booking
      if (isBookFirstCohort) {
        await interviewOps.openBookInterviewModal(
          PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW,
          interviewAvailability
        );
        return;
      }

      await applicationOps.handleFetchApplicationPreferences(async (preferences) => {
        applicationOps.setApplicationPreferences(preferences);
        await interviewOps.openBookInterviewModal(
          PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW,
          interviewAvailability
        );
      });
    },

    hasInterviewFromConnectedListing: () => {
      navigationOps.handleHasInterviewFromConnectedListingAction();
    },
  };

  /**
   * Execute the actual primary action based on action type.
   * This is separated from handlePrimaryAction to allow reuse after agreement signing.
   */
  const executePrimaryAction = async () => {
    // Easy scheduling: Intercept APPLY and BOOK_INTERVIEW actions for eligible placements
    const isEasySchedulingAction =
      (primaryAction.type === PLACEMENT_ACTION_TYPE.APPLY && derivedData.isJobRequestEnabled) ||
      primaryAction.type === PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW;

    if (isEasySchedulingPlacement && isEasySchedulingAction) {
      await flows.easySchedulingInterviewRequest();
      return;
    }

    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (primaryAction.type) {
      case PLACEMENT_ACTION_TYPE.APPLY: {
        await flows.applyToJob();
        break;
      }

      case PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW: {
        await flows.bookInterview();
        break;
      }

      case PLACEMENT_ACTION_TYPE.RESCHEDULE_INTERVIEW: {
        await flows.rescheduleInterview();
        break;
      }

      case PLACEMENT_ACTION_TYPE.LATE_CANCEL: {
        flows.cancelPastInterview();
        break;
      }

      case PLACEMENT_ACTION_TYPE.COMPLETE_PROFILE: {
        flows.completeProfile();
        break;
      }

      case PLACEMENT_ACTION_TYPE.UPLOAD_LICENSE: {
        flows.uploadLicense();
        break;
      }

      case PLACEMENT_ACTION_TYPE.ADD_QUALIFICATION: {
        await flows.addQualification();
        break;
      }

      case PLACEMENT_ACTION_TYPE.UPLOAD_DOCUMENT: {
        flows.uploadDocuments();
        break;
      }

      case PLACEMENT_ACTION_TYPE.APPLY_VIA_WEBLINK: {
        await flows.applyViaExternalLink();
        break;
      }

      case PLACEMENT_ACTION_TYPE.TALK_TO_RECRUITER: {
        await flows.talkToRecruiter();
        break;
      }

      case PLACEMENT_ACTION_TYPE.TALK_TO_RECRUITER_NEXT_STEPS: {
        flows.talkToRecruiterNextSteps();
        break;
      }

      case PLACEMENT_ACTION_TYPE.UPLOAD_CREDENTIAL: {
        flows.uploadCredential();
        break;
      }

      case PLACEMENT_ACTION_TYPE.VERIFY_ATTENDANCE: {
        flows.verifyInterviewAttendance();
        break;
      }

      case PLACEMENT_ACTION_TYPE.UPDATE_ETA: {
        flows.updateInterviewEta();
        break;
      }

      case PLACEMENT_ACTION_TYPE.HAS_CONNECTED_LISTING_INTERVIEW: {
        flows.hasInterviewFromConnectedListing();
        break;
      }

      default: {
        // No action - user has already applied or action is disabled
        break;
      }
    }
  };

  const handlePrimaryAction = async () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_PRIMARY_CTA_CLICKED, {
      ...derivedData.eventContext,
      actionType: primaryAction.type,
      actionLabel: primaryAction.label,
    });

    // Job Search Agreement intercept: If agreement is required and this is a booking action,
    // show the agreement modal first and continue to booking after signing.
    // For TALK_TO_RECRUITER, only show agreement if user hasn't started screening process.
    const isBookingAction = shouldShowAgreementBeforeBooking(primaryAction.type);
    const shouldSkipAgreementForScreening =
      primaryAction.type === PLACEMENT_ACTION_TYPE.TALK_TO_RECRUITER &&
      placementCandidate.screeningStatus !== PlacementCandidateScreeningStatus.NO_SCREENING &&
      placementCandidate.screeningStatus !== undefined;

    if (isJobSearchAgreementRequired && isBookingAction && !shouldSkipAgreementForScreening) {
      pendingAgreementActionRef.current = executePrimaryAction;
      state.modals.jobSearchAgreement.openModal();
      return;
    }

    await executePrimaryAction();
  };

  const allowedRestrictedActions: string[] = [
    PLACEMENT_ACTION_TYPE.RESCHEDULE_INTERVIEW,
    PLACEMENT_ACTION_TYPE.UPLOAD_DOCUMENT,
    PLACEMENT_ACTION_TYPE.UPLOAD_CREDENTIAL,
    PLACEMENT_ACTION_TYPE.UPLOAD_LICENSE,
    PLACEMENT_ACTION_TYPE.VERIFY_ATTENDANCE,
    PLACEMENT_ACTION_TYPE.HAS_CONNECTED_LISTING_INTERVIEW,
  ];

  const shouldDisablePrimaryAction =
    placementCandidate.isRestricted === true &&
    primaryAction.type !== undefined &&
    !allowedRestrictedActions.includes(primaryAction.type);

  const primaryActionProps = {
    type: primaryAction.type,
    label: primaryAction.label,
    description: primaryAction.description,
    warning: primaryAction.warning,
    onClick: handlePrimaryAction,
    disabled: shouldDisablePrimaryAction || primaryAction.disabled,
    isLoading:
      applicationOps.isCreatingPlacementApplication ||
      interviewOps.isCreatingInterview ||
      isCreatingQualification,
    startIcon: primaryAction.startIcon,
    additionalContent: primaryAction.additionalContent,
  };

  const workplaceStartHour = calculateWorkplaceStartHour({
    interview: derivedData.interview,
    selectedInterviewTime: interviewOps.selectedInterviewTime,
    workerTimezone: derivedData.workerTimezone,
    workplaceTimezone: placementDetails.workplace.tmz,
  });

  const isBookFirstCohort = bookingReorderingCohort === BOOKING_REORDERING_COHORT.BOOK_FIRST;

  // Status calculations
  const hasAcceptedApplication = isDefined(
    placementDetails.applications?.find(
      (application) => application.attributes.status === PlacementApplicationStatus.ACCEPTED
    )
  );
  const isInterviewBooked = hasBookedInterview(placementDetails);
  const canScheduleInterviewFromDeepLink = interviewAvailability.length > 0;
  const actionType: PlacementActionType = isInterviewBooked
    ? PLACEMENT_ACTION_TYPE.RESCHEDULE_INTERVIEW
    : PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW;

  // Return clean interface
  return {
    // Primary action
    primaryAction,
    primaryActionProps,

    // User flows
    flows,

    // Modal states
    modals: state.modals,

    // Interview state & operations
    interview: {
      selectedInterviewTime: interviewOps.selectedInterviewTime,
      setSelectedInterviewTime: interviewOps.setSelectedInterviewTime,
      bookInterviewModalType: interviewOps.bookInterviewModalType,
      interviewData: derivedData.interview,
      openBookInterviewModal: interviewOps.openBookInterviewModal,
      continueAfterPhoneConfirmation: interviewOps.continueAfterPhoneConfirmation,
      handleBookOrRescheduleInterview: interviewOps.handleBookOrRescheduleInterview,
      isLateCancellation: derivedData.isLateCancellation,
      isReschedulingPastInterview: derivedData.isReschedulingPastInterview,
      checkLocationPermissionsAfterInterviewBooking:
        interviewOps.checkLocationPermissionsAfterInterviewBooking,
      isOsWithoutAlwaysAllow: interviewOps.isOsWithoutAlwaysAllow,
      isRescheduleToFutureDay: interviewOps.isRescheduleToFutureDay,
      isRescheduleForToday: interviewOps.isRescheduleForToday,
      isBookingFromSubmitDocumentsLater: state.interviewState.isBookingFromSubmitDocumentsLater,
      setIsBookingFromSubmitDocumentsLater:
        state.interviewState.setIsBookingFromSubmitDocumentsLater,
      workplaceStartHour,
      isBookFirstCohort,
    },

    // Application state & operations
    application: {
      handleApply: applicationOps.handleApply,
      applicationPreferences: applicationOps.applicationPreferences,
      setApplicationPreferences: applicationOps.setApplicationPreferences,
      applicationManagementType: applicationOps.applicationManagementType,
      setApplicationManagementType: applicationOps.setApplicationManagementType,
      openExternalApplication: applicationOps.openExternalApplication,
      handleFetchApplicationPreferences: applicationOps.handleFetchApplicationPreferences,
      pendingActionRef: applicationOps.pendingActionRef,
    },

    // Derived data for modals
    derivedData: {
      showCallMeNow: derivedData.showCallMeNow,
      workerId: derivedData.workerId,
      workerTimezone: derivedData.workerTimezone,
      interview: derivedData.interview,
      unverifiedBookedInterview: derivedData.unverifiedBookedInterview,
      highestRankingSharedQualification: derivedData.highestRankingSharedQualification,
      requirement: derivedData.requirement,
      isProfilePending: derivedData.isProfilePending,
      workplaceStartHour: derivedData.workplaceStartHour,
      isSchedulingTodayAfterWorkplaceStartHour:
        derivedData.isSchedulingTodayAfterWorkplaceStartHour,
      startsInWorkplaceToday: derivedData.startsInWorkplaceToday,
      eventContext: derivedData.eventContext,
      hasMissingOrPendingDocuments,
      isSubmitHiringDocumentsLaterEnabled: derivedData.isSubmitHiringDocumentsLaterEnabled,
      inProgressInterview: derivedData.inProgressInterview,
      hasInProgressInterview: derivedData.hasInProgressInterview,
      isEligibleForScreenLastScreeningModal: derivedData.isEligibleForScreenLastScreeningModal,
      isEligibleForBookFirstConfirmation: derivedData.isEligibleForBookFirstConfirmation,
      isRecruiterScreeningEnabled: derivedData.isRecruiterScreeningEnabled,
      connectedPlacementId: derivedData.connectedPlacementId,
      hasConnectedListingInterview: derivedData.hasConnectedListingInterview,
      isConnectedListingInterviewInOtherWorkplace:
        derivedData.isConnectedListingInterviewInOtherWorkplace,
    },

    // Recruiter flow
    recruiterScreeningCallFlowState: navigationOps.recruiterScreeningCallFlowState,

    // Childcare chat state
    childcareChatState: state.childcareChatState,

    // Job Search Agreement handlers
    jobSearchAgreement: {
      handleAgreementComplete,
      handleAgreementContinueBooking,
      placementId: placementDetails.id,
    },

    // Status
    status: {
      hasAcceptedApplication,
      hasBookedInterview,
      canScheduleInterviewFromDeepLink,
      actionType,
    },

    // Loading states
    isLoading:
      applicationOps.isCreatingPlacementApplication ||
      interviewOps.isCreatingInterview ||
      isCreatingQualification ||
      isCreatingChildcareSupportChat,
  };
}

export type PlacementActionsReturn = ReturnType<typeof usePlacementActions>;
